/**
 * Engine calculations region inspect view per scope §6.5. Atlas Day 3a P4.
 *
 * Region-scope inspect — Engine calculations is single-region-with-internal-
 * sections; layer content surfaces at region scope rather than per-value
 * (per three-state layer applicability principle — most layers N/A at
 * value-altitude for RPCs).
 *
 * Per scope §6.5 + Atlas Day 3a P0 #8 verification: 5 engine RPCs cover
 * Primary calculations (calculate_profitability + _tiers + _breakdown_extras)
 * + Forward-looking projections (forecast_restock + calculate_po_impact).
 * Days 68-72 refactor commit references in L7 (point-in-time-attested per
 * Atlas Day 2.5 discipline candidate).
 *
 * Cross-links: ADR 36+38 hardcoded prod-domain URL constants from
 * atlasUrls.ts (Atlas Day 2 P5).
 */

import type { JSX } from 'react';
import {
  URL_SETTINGS_PARAMS,
  URL_SETTINGS_CHANNELS_AND_PATHS,
  URL_SETTINGS_COST_LINE_ITEMS,
  URL_DEV_ENGINE_EDITOR_WEIGHT_COMPONENTS,
  URL_DIRECTORY_MANUFACTURERS,
} from '../../../lib/atlas/atlasUrls';
import { ENGINE_CALCULATIONS_PROPAGATION } from '../../../lib/atlas/propagationMaps';
import InspectShell from './InspectShell';
import LayerSection from './LayerSection';
import ComposabilityReservedLayer from './ComposabilityReservedLayer';

export default function EngineCalculationsInspect(): JSX.Element {
  return (
    <InspectShell
      subRegionName="Engine calculations"
      surfaceLine="The math the engine performs. Primary calculations cover present-state profitability across single-quantity and tiered modes + breakdown inspection. Forward-looking projections cover future-state restock and PO impact."
    >
      <LayerSection layer={4} title="Math vs judgment">
        <p>
          Math-prominent region per scope §6.5. Per-RPC formula summary via
          &quot;Math details&quot; deeper drill (CF 25 expanded scope home — Atlas Day 3a
          ships scaffolding; full formula expansion + dependency graph fills the deeper drill
          modal at a future build day). Layer 4 inspect at peer-altitude summarizes here.
        </p>
      </LayerSection>

      <LayerSection layer={5} title="Mechanical vs strategic">
        <p>
          Mechanical at region scope per scope §6.5. RPC bodies are formula structure;
          strategic choices live in substrate that the RPCs read (per-entity params + business
          defaults + path mechanics + Buy Score weight components — all at substrate row).
        </p>
        <p>
          <strong>Velocity resolution inline sub-content</strong> (per scope resolution-chain
          sub-principle §4.4): velocity resolution surfaces in mechanical-layer inspect content
          as a pre-calc resolver, not as a named sub-section. Velocity resolution lives at
          transformation × calculation regardless of code home; surfaces inline here per
          scope-locked (β) decision.
        </p>
        <p>
          <strong>Cost-of-goods resolution inline sub-content</strong> (same resolution-chain
          treatment, added 2026-06-10 with the manufacturer-discount wiring): the
          manufacturer↔distributor discount (<code>manufacturer_distributors.discount_pct</code>,
          Directory-editable) resolves as a pre-calc cost-of-goods input —
          <code>cost_product = cost × (1 − discount)</code>. Goods-only (never multiplies
          shipping / prep / misc), and in tier mode it stacks on the tier&apos;s{' '}
          <code>unit_cost</code>. Wired into all four cost RPCs (
          <code>calculate_profitability</code> + <code>_tiers</code> +{' '}
          <code>_breakdown_extras</code> + <code>calculate_po_impact</code>); a product with no
          manufacturer link or a 0% discount resolves to unchanged cost.
        </p>
      </LayerSection>

      <LayerSection layer={6} title="Operational vs ownership">
        <p>
          Region-scope <strong>code-level authority altitude</strong> per scope §6.5. This
          region contains engine RPC bodies — mathematical structure encoded as code, not
          configuration. Authority altitude: code-level. Edits require code change, not data
          change; outside the operational/ownership-of-configuration distinction that applies
          elsewhere on the map.
        </p>
      </LayerSection>

      <LayerSection layer={7} title="Trace">
        <p>
          Region-scope via git history per scope §6.5. Per-RPC commit references (re-verified
          2026-06-10 per point-in-time disk-state attestation discipline — the Fix Pass B
          manufacturer-discount wiring redefined the four cost RPCs; future build days touching
          engine RPCs trigger L7 re-verification):
        </p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          <li>
            <code>calculate_profitability</code> — Day 68 P3.1 refactor:{' '}
            <code>20260517000001_day68_calculate_profitability_normalized_buy_score_map_enforcement.sql</code>
            ; live definition since Fix Pass B:{' '}
            <code>20260613000010_fixB_manufacturer_discount.sql</code>
          </li>
          <li>
            <code>calculate_profitability_tiers</code> — Day 69 P3.2 refactor:{' '}
            <code>20260518000001_day69_calculate_profitability_tiers_normalized.sql</code>; live
            definition since Fix Pass B: <code>20260613000010_fixB_manufacturer_discount.sql</code>
          </li>
          <li>
            <code>calculate_breakdown_extras</code> — Day 70 P3.3 refactor:{' '}
            <code>20260519000001_day70_calculate_breakdown_extras_normalized.sql</code>; live
            definition since Fix Pass B: <code>20260613000010_fixB_manufacturer_discount.sql</code>
          </li>
          <li>
            <code>forecast_restock</code> — Day 71 P3.4 refactor:{' '}
            <code>20260520000001_day71_forecast_restock_velocity_resolution_chain.sql</code>{' '}
            (not a cost RPC — untouched by Fix Pass B)
          </li>
          <li>
            <code>calculate_po_impact</code> — Day 72 P3.5 refactor:{' '}
            <code>20260521000001_day72_calculate_po_impact.sql</code>; live definition since Fix
            Pass B: <code>20260613000010_fixB_manufacturer_discount.sql</code>
          </li>
        </ul>
      </LayerSection>

      <LayerSection layer={8} title="Propagation">
        <p>
          Curated propagation map (per-entry <em>verified-at</em> staleness markers per scope
          §8 — the RPC-bodies entry attested 2026-05-27, the manufacturer-discount entry
          2026-06-10):
        </p>
        <ul className="list-disc pl-5 text-xs space-y-2">
          {ENGINE_CALCULATIONS_PROPAGATION.map((entry) => (
            <li key={entry.field}>
              <strong>
                <code>{entry.field}</code>
              </strong>{' '}
              edits affect:
              <ul className="list-circle pl-5 mt-1 space-y-0.5">
                {entry.affects.map((eff) => (
                  <li key={eff}>{eff}</li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      </LayerSection>

      <LayerSection layer={9} title="Reversibility">
        <p>
          Region-scope <strong>structurally-irreversible</strong> per scope §6.5. Reversibility:
          code-level. Edits via deployment; prior state recoverable only via git history.
          Past <code>product_profitability</code> cache rows hold prior-engine-version
          calculations until recomputed.
        </p>
      </LayerSection>

      <ComposabilityReservedLayer />

      <LayerSection layer={11} title="Positional contribution">
        <p>
          Substantive-contribution at region scope per scope §6.5 L11. Engine calculations
          realize the mathematical assertions in the title block. Primary calculations realize
          the buying-criteria comparison (ROI / margin / velocity thresholds). Forward-looking
          projections realize the restock-tiered posture.
        </p>
      </LayerSection>

      {/* The L4 slot-number on this footer is the shared Atlas Day-3a convention —
          all six transformation-row inspect views render their Cross-links section
          as `layer={4}` after L11 (grep `cross-links footer`). It is a footer slot,
          not a sequence position; the body's first line says so for readers. */}
      <LayerSection layer={4 /* cross-links footer */} title="Cross-links (indirect — RPCs are code, not data)">
        <p className="text-2xs text-ink-muted">
          (Cross-links render as the closing footer on every transformation-row inspect; the
          &quot;L4.&quot; slot-number is the shared footer convention, not a sequence position.)
        </p>
        <p>
          Engine calculations region not directly editable. Indirect cross-links to substrate
          the RPCs read:
        </p>
        <ul className="list-disc pl-5 text-xs space-y-0.5">
          <li>
            <a href={URL_SETTINGS_PARAMS} className="text-accent hover:underline">
              Settings → Params &amp; Defaults
            </a>{' '}
            — business-level engine inputs
          </li>
          <li>
            <a href={URL_SETTINGS_CHANNELS_AND_PATHS} className="text-accent hover:underline">
              Settings → Channels &amp; Paths
            </a>{' '}
            — path mechanics + marketplace fees
          </li>
          <li>
            <a href={URL_SETTINGS_COST_LINE_ITEMS} className="text-accent hover:underline">
              Settings → Cost Line Items
            </a>{' '}
            — per-path cost component substrate
          </li>
          <li>
            <a href={URL_DEV_ENGINE_EDITOR_WEIGHT_COMPONENTS} className="text-accent hover:underline">
              Engine Editor → Weight Components
            </a>{' '}
            — bswc metric catalog
          </li>
          <li>
            <a href={URL_DIRECTORY_MANUFACTURERS} className="text-accent hover:underline">
              Directory → Manufacturers
            </a>{' '}
            — manufacturer↔distributor discount (<code>discount_pct</code>), the cost-of-goods
            input wired at Fix Pass B
          </li>
        </ul>
      </LayerSection>
    </InspectShell>
  );
}
